import React from "react";
import { Box, Container, Text, Flex, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

const MotionBox = motion(Box);
const steps = [
  { label: "Course", desc: "Subject grouping" },
  { label: "Subject", desc: "Core discipline" },
  { label: "Unit", desc: "Topic cluster" },
  { label: "Section", desc: "Focused area" },
  { label: "Block", desc: "Study-ready content" },
];

const knowItems = [
  "What to study",
  "What comes next",
  "What actually matters — in exams and beyond",
];

export const Structure = () => {
  return (
    <Box
      bg="#E9FFFF"
      py={{ base: "64px", md: "96px" }}
      position="relative"
      overflow="hidden"
    >
      {/* Decorative circle */}
      <Box
        position="absolute"
        bottom="-100px"
        right="-100px"
        w={{ base: "280px", md: "400px" }}
        h={{ base: "280px", md: "400px" }}
        borderRadius="50%"
        border="48px solid #0095C8"
        opacity="0.1"
        pointerEvents="none"
      />

      <Container maxW="1200px" px={{ base: "5", md: "8" }}>

        {/* Section header */}
        <Flex direction="column" align="center" textAlign="center" mb={{ base: 12, md: 16 }}>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="0.8125rem"
            fontWeight="600"
            letterSpacing="0.1em"
            textTransform="uppercase"
            color="#0095C8"
            mb={3}
          >
            The System
          </Text>
          <Text
            as="h2"
            fontFamily="'Public Sans', sans-serif"
            fontWeight="600"
            fontSize={{ base: "1.75rem", md: "2.375rem" }}
            color="#293452"
            lineHeight="1.25"
            maxW="600px"
            mb={4}
          >
            From syllabus to bedside —{" "}
            <Box as="span" color="#0095C8">with structure</Box>
          </Text>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize={{ base: "0.9375rem", md: "1.0625rem" }}
            color="rgba(41,52,82,0.6)"
            maxW="480px"
            lineHeight="1.7"
          >
            31Gauge organises the entire nursing curriculum into a clear system.
          </Text>
        </Flex>

        {/* Flow diagram */}
        <Box mb={{ base: 12, md: 16 }}>
          <Flex
            align="center"
            justify="center"
            gap={{ base: 1, md: 2 }}
            flexWrap="wrap"
            rowGap={4}
          >
            {steps.map((step, i) => (
              <React.Fragment key={step.label}>
                <MotionBox
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                >
                  <VStack
                    spacing={1}
                    p={{ base: 4, md: 5 }}
                    bg="white"
                    borderRadius="12px"
                    border="1px solid rgba(0,149,200,0.18)"
                    boxShadow="0 2px 12px rgba(41,52,82,0.06)"
                    minW={{ base: "90px", md: "120px" }}
                    textAlign="center"
                    _hover={{
                      boxShadow: "0 8px 24px rgba(0,149,200,0.15)",
                      transform: "translateY(-3px)",
                      borderColor: "#0095C8",
                    }}
                    transition="all 0.3s cubic-bezier(0.4,0,0.2,1)"
                    cursor="default"
                  >
                    <Box
                      w="32px"
                      h="32px"
                      borderRadius="8px"
                      background="linear-gradient(135deg, #0095C8, #016293)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mb={1}
                    >
                      <Text
                        fontFamily="'Public Sans', sans-serif"
                        fontWeight="700"
                        fontSize="0.75rem"
                        color="white"
                      >
                        {String(i + 1).padStart(2, "0")}
                      </Text>
                    </Box>
                    <Text
                      fontFamily="'Public Sans', sans-serif"
                      fontWeight="600"
                      fontSize={{ base: "0.875rem", md: "1rem" }}
                      color="#293452"
                    >
                      {step.label}
                    </Text>
                    <Text
                      fontFamily="'Inter', sans-serif"
                      fontSize={{ base: "0.7rem", md: "0.75rem" }}
                      color="rgba(41,52,82,0.5)"
                    >
                      {step.desc}
                    </Text>
                  </VStack>
                </MotionBox>

                {i < steps.length - 1 && (
                  <Box color="#0095C8" opacity="0.5" flexShrink={0}>
                    <ChevronRight size={20} strokeWidth={2} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Box>

        {/* So you always know */}
        <Flex direction="column" align="center" gap={4}>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="0.9375rem"
            color="rgba(41,52,82,0.6)"
            textAlign="center"
            fontWeight="500"
          >
            So you always know:
          </Text>
          <Flex
            gap={4}
            justify="center"
            flexWrap="wrap"
          >
            {knowItems.map((item, i) => (
              <MotionBox
                key={item}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Box
                  px={5}
                  py={3}
                  bg="white"
                  borderRadius="full"
                  border="1px solid rgba(0,149,200,0.2)"
                  boxShadow="0 1px 6px rgba(0,149,200,0.08)"
                >
                  <Text
                    fontFamily="'Inter', sans-serif"
                    fontSize="0.875rem"
                    color="#293452"
                    fontWeight="500"
                    whiteSpace={{ base: "normal", md: "nowrap" }}
                    textAlign="center"
                  >
                    {item}
                  </Text>
                </Box>
              </MotionBox>
            ))}
          </Flex>

          {/* Tagline */}
          <MotionBox
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            mt={4}
          >
            <Text
              fontFamily="'Public Sans', sans-serif"
              fontWeight="600"
              fontSize={{ base: "1.0625rem", md: "1.25rem" }}
              color="#293452"
              textAlign="center"
            >
              No gaps.{" "}
              <Box as="span" color="#0095C8">No second-guessing.</Box>
            </Text>
          </MotionBox>
        </Flex>

      </Container>
    </Box>
  );
};
